/**
 * IMP-2466: what went wrong when a student opened their invitation link.
 *
 * The registration page used to forward the backend's raw message to the login page, and the
 * login page ignored it and always said "User is already registered, please login". A rate-limit
 * refusal (HTTP 429) skipped even that and dropped the student on a bare login form. Support
 * could not tell the failures apart, and students with no password had no way forward.
 *
 * Failures are now reduced to a fixed code that travels in `?error=`, and the login page maps
 * the code back to fixed copy. The login page never renders the query-string text itself, so a
 * crafted link cannot put arbitrary words on the sign-in screen.
 */

export const REGISTRATION_LINK_ERROR = Object.freeze({
  ALREADY_REGISTERED: 'already-registered',
  INVALID_LINK: 'invalid-link',
  RATE_LIMITED: 'rate-limited',
  UNAVAILABLE: 'unavailable',
});

// The backend's exact text for an account that has already been set up
// (auth.service.ts getUserEmail / registration).
const BACKEND_ALREADY_REGISTERED = 'User is already registered, please login';

const MAX_RETRY_AFTER_SECONDS = 24 * 60 * 60;

/**
 * Code for a getUserEmail reply that arrived with HTTP 200 but a non-200 `code` in the body —
 * the backend reports not-found and already-registered this way.
 */
export function registrationLinkErrorFromBody(body) {
  const code = body?.code;
  if (code === 429) return REGISTRATION_LINK_ERROR.RATE_LIMITED;
  if (code === 404) return REGISTRATION_LINK_ERROR.INVALID_LINK;
  if (code === 400 && body?.msg === BACKEND_ALREADY_REGISTERED) return REGISTRATION_LINK_ERROR.ALREADY_REGISTERED;
  return REGISTRATION_LINK_ERROR.UNAVAILABLE;
}

/** Code for a rejected axios request (non-2xx status, or no response at all). */
export function registrationLinkErrorFromAxiosError(err) {
  if (err?.response?.status === 429) return REGISTRATION_LINK_ERROR.RATE_LIMITED;
  return REGISTRATION_LINK_ERROR.UNAVAILABLE;
}

/** Retry-After in whole seconds if the header is a sane delta-seconds value, else undefined. */
export function retryAfterSeconds(err) {
  const raw = err?.response?.headers?.['retry-after'];
  if (typeof raw !== 'string' && typeof raw !== 'number') return undefined;
  const text = String(raw).trim();
  if (!/^\d{1,6}$/.test(text)) return undefined;
  const seconds = Number(text);
  return seconds >= 1 && seconds <= MAX_RETRY_AFTER_SECONDS ? seconds : undefined;
}

// The rate-limit copy is deliberately neutral: the backend returns the same 429 whether one
// account used up its own attempts or the whole network hit its cap, so the page cannot tell
// which, and "too many people" would be false for the single-student case.

/** Login-page path carrying a registration-link failure. */
export function loginPathForRegistrationError(code, retryAfter) {
  const params = new URLSearchParams({ error: code });
  if (code === REGISTRATION_LINK_ERROR.RATE_LIMITED && retryAfter !== undefined) {
    params.set('retryAfter', String(retryAfter));
  }
  return `/?${params.toString()}`;
}

export function waitPhrase(retryAfterParam) {
  const text =
    typeof retryAfterParam === 'string' || typeof retryAfterParam === 'number' ? String(retryAfterParam).trim() : '';
  if (!/^\d{1,6}$/.test(text)) return 'a little while';
  const seconds = Number(text);
  if (seconds < 1 || seconds > MAX_RETRY_AFTER_SECONDS) return 'a little while';
  const minutes = Math.ceil(seconds / 60);
  return minutes <= 1 ? 'about a minute' : `about ${minutes} minutes`;
}

/**
 * Fixed copy for a code read from the login page's query string. Unknown or tampered values
 * get the generic message — never the value itself.
 */
export function registrationLinkErrorMessage(code, retryAfterParam) {
  switch (code) {
    // BACKEND_ALREADY_REGISTERED is the legacy form: the raw backend text was forwarded before IMP-2466.
    case REGISTRATION_LINK_ERROR.ALREADY_REGISTERED:
    case BACKEND_ALREADY_REGISTERED:
      return 'This account is already set up. Please log in, or use "Forgot password?" to choose a new password.';
    case REGISTRATION_LINK_ERROR.INVALID_LINK:
      return 'This invitation link is no longer valid. Please contact your program staff for a new link.';
    case REGISTRATION_LINK_ERROR.RATE_LIMITED:
      return `Too many sign-up attempts right now. Please wait ${waitPhrase(
        retryAfterParam
      )}, then open your invitation link again.`;
    default:
      return "We couldn't open your invitation link. Please try again, or contact your program staff.";
  }
}

/** Toast copy when the registration form itself is refused with HTTP 429. */
export function registrationSubmitRateLimitedMessage(retryAfter) {
  return `Too many sign-up attempts right now. Please wait ${waitPhrase(retryAfter)}, then press Submit again.`;
}
